/**
 * PublicBlockRenderer — renders a Studio-built page's `content_blocks` for the
 * PUBLIC (unauthenticated) site at triveny.ai (and any org's custom domain).
 *
 * Applies the org's brand: primary color + font come from brand_settings and
 * wp_design_tokens (returned by /api/public/site/*), so buttons/accents and
 * typography match the org's design system. Self-contained inline styles; no
 * app chrome beyond a small top-right "Log in" link.
 *
 * Block shape (wp_pages.content_blocks, normalized by Studio):
 *   { id, blockType|type, content, styles, settings, sortOrder, isVisible }
 * Unknown block types fall back to a best-effort headline/text render.
 */

import { useState, type CSSProperties } from 'react';
import TrivenyHero, { type Pillar } from './TrivenyHero';
import { config } from '../../../config';

type AnyBlock = Record<string, any>;

interface DesignToken {
  token_group: string;
  token_name: string;
  token_value: string;
}

interface PublicSitePage {
  title?: string;
  brand_settings?: Record<string, any>;
  design_tokens?: DesignToken[];
  content_blocks?: AnyBlock[];
}

interface Theme {
  primary: string;
  ink: string;
  muted: string;
  font: string;
}

const DEFAULTS: Theme = {
  primary: '#46b1e5',
  ink: '#14171c',
  muted: '#5b6470',
  font: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
};

// Derive a Theme from brand_settings + design tokens. brand_settings wins
// (explicit), then color/typography tokens by common names, then defaults.
function deriveTheme(page: PublicSitePage): Theme {
  const bs = page.brand_settings || {};
  const tokens = page.design_tokens || [];
  const colorToken = (...names: string[]) =>
    tokens.find((t) => t.token_group === 'color' && names.includes((t.token_name || '').toLowerCase()))?.token_value;
  const typeToken = (...names: string[]) =>
    tokens.find((t) => t.token_group === 'typography' && names.includes((t.token_name || '').toLowerCase()))
      ?.token_value;

  return {
    primary: bs.primaryColor || colorToken('primary', 'accent', 'brand') || DEFAULTS.primary,
    ink: bs.textColor || colorToken('ink', 'text', 'foreground') || DEFAULTS.ink,
    muted: colorToken('muted', 'secondary-text') || DEFAULTS.muted,
    font: bs.fontFamily || typeToken('body', 'font', 'sans', 'display') || DEFAULTS.font,
  };
}

function toCss(styles: AnyBlock | undefined | null): CSSProperties {
  if (!styles || typeof styles !== 'object') return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(styles)) {
    if (v === undefined || v === null || v === '') continue;
    if (k === 'backgroundGradient') out.background = v;
    else out[k] = v;
  }
  return out as CSSProperties;
}

function asArray(v: any): any[] {
  return Array.isArray(v) ? v : [];
}

const sectionBase: CSSProperties = { padding: '64px 24px', width: '100%', boxSizing: 'border-box' };
const container: CSSProperties = { maxWidth: 1080, margin: '0 auto' };

function Buttons({ buttons, t }: { buttons: any[]; t: Theme }) {
  if (!buttons.length) return null;
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 24 }}>
      {buttons.map((b, i) => {
        const primary = (b.style || b.variant || (i === 0 ? 'primary' : 'secondary')) === 'primary';
        return (
          <a
            key={i}
            href={b.url || b.href || '#'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '11px 22px',
              borderRadius: 999,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: 'none',
              background: primary ? t.primary : 'transparent',
              color: primary ? '#fff' : t.ink,
              border: primary ? 'none' : `1px solid ${t.ink}`,
            }}
          >
            {b.text || b.label || 'Learn more'}
          </a>
        );
      })}
    </div>
  );
}

function Block({ block, t }: { block: AnyBlock; t: Theme }) {
  const type = block.blockType || block.type || 'unknown';
  const c: AnyBlock = block.content || {};
  const style: CSSProperties = { ...sectionBase, ...toCss(block.styles) };

  switch (type) {
    case 'hero': {
      const bg = c.backgroundImage;
      return (
        <section
          style={{
            ...style,
            textAlign: 'center',
            padding: '120px 24px',
            ...(bg
              ? {
                  backgroundImage: `linear-gradient(rgba(255,255,255,.78),rgba(255,255,255,.78)), url(${bg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : {}),
          }}
        >
          <div style={container}>
            <h1 style={{ fontSize: 52, lineHeight: 1.05, letterSpacing: '-0.03em', color: t.ink, margin: 0, fontWeight: 700 }}>
              {c.headline || c.title}
            </h1>
            {(c.subheadline || c.subtitle) && (
              <p style={{ fontSize: 19, color: t.muted, maxWidth: 640, margin: '18px auto 0', lineHeight: 1.5 }}>
                {c.subheadline || c.subtitle}
              </p>
            )}
            <Buttons buttons={asArray(c.ctaButtons || c.buttons)} t={t} />
          </div>
        </section>
      );
    }

    case 'heading': {
      const level = Math.min(Math.max(parseInt(c.level, 10) || 2, 1), 6);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      const sizes: Record<number, number> = { 1: 44, 2: 34, 3: 26, 4: 21, 5: 18, 6: 16 };
      return (
        <section style={{ ...style, padding: '32px 24px' }}>
          <div style={{ ...container, textAlign: (toCss(block.styles).textAlign as any) || 'center' }}>
            <Tag style={{ fontSize: sizes[level], color: t.ink, margin: 0, fontWeight: 700 }}>{c.text || c.title}</Tag>
          </div>
        </section>
      );
    }

    case 'features': {
      const items = asArray(c.features || c.items);
      return (
        <section style={style}>
          <div style={container}>
            {(c.title || c.headline) && (
              <h2 style={{ fontSize: 32, textAlign: 'center', color: t.ink, margin: '0 0 40px' }}>{c.title || c.headline}</h2>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 28 }}>
              {items.map((f, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  {f.icon && <div style={{ fontSize: 30, marginBottom: 10 }}>{f.icon}</div>}
                  <h3 style={{ fontSize: 18, color: t.ink, margin: '0 0 8px' }}>{f.title}</h3>
                  <p style={{ fontSize: 15, color: t.muted, lineHeight: 1.5, margin: 0 }}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'cta': {
      return (
        <section style={{ ...style, textAlign: 'center', background: toCss(block.styles).background || '#f6f8fa' }}>
          <div style={container}>
            <h2 style={{ fontSize: 32, color: t.ink, margin: 0 }}>{c.title || c.headline}</h2>
            {c.description && <p style={{ fontSize: 17, color: t.muted, margin: '14px auto 0', maxWidth: 600 }}>{c.description}</p>}
            <Buttons buttons={asArray(c.buttons || c.ctaButtons)} t={t} />
          </div>
        </section>
      );
    }

    case 'button': {
      return (
        <section style={{ ...style, padding: '24px', textAlign: 'center' }}>
          <Buttons buttons={[{ text: c.text || c.label, url: c.url || c.href, style: 'primary' }]} t={t} />
        </section>
      );
    }

    case 'testimonials': {
      const items = asArray(c.testimonials || c.items);
      return (
        <section style={style}>
          <div style={container}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
              {items.map((tm, i) => (
                <blockquote key={i} style={{ margin: 0, padding: 24, background: '#f6f8fa', borderRadius: 12 }}>
                  <p style={{ fontSize: 16, color: t.ink, lineHeight: 1.5, margin: 0 }}>“{tm.quote}”</p>
                  <footer style={{ marginTop: 14, fontSize: 14, color: t.muted }}>
                    {tm.author}
                    {(tm.role || tm.company) && ` — ${[tm.role, tm.company].filter(Boolean).join(', ')}`}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'faq': {
      const items = asArray(c.faqs || c.items);
      return (
        <section style={style}>
          <div style={{ ...container, maxWidth: 760 }}>
            {(c.title || c.headline) && <h2 style={{ fontSize: 32, textAlign: 'center', color: t.ink, margin: '0 0 32px' }}>{c.title || c.headline}</h2>}
            {items.map((f, i) => (
              <div key={i} style={{ padding: '18px 0', borderBottom: '1px solid #e6e8ec' }}>
                <h3 style={{ fontSize: 17, color: t.ink, margin: '0 0 6px' }}>{f.question}</h3>
                <p style={{ fontSize: 15, color: t.muted, lineHeight: 1.5, margin: 0 }}>{f.answer}</p>
              </div>
            ))}
          </div>
        </section>
      );
    }

    case 'pricing': {
      const plans = asArray(c.plans || c.items);
      return (
        <section style={style}>
          <div style={container}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
              {plans.map((p, i) => (
                <div key={i} style={{ padding: 28, border: `1px solid ${p.isPopular ? t.primary : '#e6e8ec'}`, borderRadius: 14, textAlign: 'center' }}>
                  <h3 style={{ fontSize: 18, color: t.ink, margin: 0 }}>{p.name}</h3>
                  <div style={{ fontSize: 36, fontWeight: 700, color: t.ink, margin: '12px 0' }}>
                    {p.price}
                    {p.period && <span style={{ fontSize: 14, color: t.muted, fontWeight: 400 }}>/{p.period}</span>}
                  </div>
                  {asArray(p.features).map((feat, j) => (
                    <div key={j} style={{ fontSize: 14, color: t.muted, padding: '4px 0' }}>{feat}</div>
                  ))}
                  {p.ctaText && <Buttons buttons={[{ text: p.ctaText, url: p.ctaUrl, style: 'primary' }]} t={t} />}
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'stats': {
      const items = asArray(c.stats || c.items);
      return (
        <section style={style}>
          <div style={{ ...container, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', gap: 24, textAlign: 'center' }}>
            {items.map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 40, fontWeight: 700, color: t.primary }}>{s.value || s.number}</div>
                <div style={{ fontSize: 15, color: t.muted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      );
    }

    case 'gallery': {
      const imgs = asArray(c.images || c.items);
      return (
        <section style={style}>
          <div style={{ ...container, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {imgs.map((im, i) => (
              <img key={i} src={im.imageUrl || im.url || im} alt={im.imageAlt || im.alt || ''} style={{ width: '100%', borderRadius: 10, display: 'block' }} />
            ))}
          </div>
        </section>
      );
    }

    case 'image': {
      if (!c.imageUrl && !c.url) return null;
      return (
        <section style={style}>
          <div style={{ ...container, textAlign: 'center' }}>
            <img src={c.imageUrl || c.url} alt={c.imageAlt || c.alt || ''} style={{ maxWidth: '100%', borderRadius: 12 }} />
            {c.caption && <p style={{ fontSize: 13, color: t.muted, marginTop: 8 }}>{c.caption}</p>}
          </div>
        </section>
      );
    }

    case 'video': {
      const url = c.videoUrl || c.url;
      if (!url) return null;
      const embed = /youtube|vimeo/.test(c.videoType || url);
      return (
        <section style={style}>
          <div style={{ ...container, maxWidth: 860 }}>
            {embed ? (
              <iframe src={url} title={c.title || 'Video'} style={{ width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: 12 }} allowFullScreen />
            ) : (
              <video src={url} controls style={{ width: '100%', borderRadius: 12 }} />
            )}
          </div>
        </section>
      );
    }

    case 'columns':
    case 'container': {
      const children = asArray(c.children || c.columns || block.children);
      if (!children.length) return null;
      return (
        <section style={style}>
          <div style={{ ...container, display: 'grid', gridTemplateColumns: `repeat(${Math.min(children.length, 4)}, 1fr)`, gap: 24 }}>
            {children.map((child, i) => (
              <div key={i}>
                <Block block={child} t={t} />
              </div>
            ))}
          </div>
        </section>
      );
    }

    case 'spacer': {
      const h = c.height || (block.styles && (block.styles as any).height) || 48;
      return <div style={{ height: typeof h === 'number' ? `${h}px` : h }} />;
    }

    case 'divider': {
      return (
        <div style={{ ...container, padding: '0 24px' }}>
          <hr style={{ border: 0, borderTop: '1px solid #e6e8ec', margin: '24px 0' }} />
        </div>
      );
    }

    case 'text':
    case 'richtext': {
      return (
        <section style={style}>
          <div style={{ ...container, maxWidth: 760, fontSize: 16, color: t.ink, lineHeight: 1.6 }}>
            {c.richText ? (
              // Org-authored content served on its own domain.
              <div dangerouslySetInnerHTML={{ __html: c.richText }} />
            ) : (
              <p style={{ margin: 0 }}>{c.text}</p>
            )}
          </div>
        </section>
      );
    }

    case 'html':
    case 'custom_html':
    case 'custom_code': {
      const html = c.html || c.code || c.customHtml || '';
      return (
        <section style={style}>
          <div style={container} dangerouslySetInnerHTML={{ __html: html }} />
        </section>
      );
    }

    case 'form': {
      return (
        <section style={style}>
          <div style={{ ...container, maxWidth: 560 }}>
            {(c.title || c.heading) && <h2 style={{ fontSize: 28, color: t.ink, margin: '0 0 6px' }}>{c.title || c.heading}</h2>}
            {c.description && <p style={{ fontSize: 15, color: t.muted, margin: '0 0 18px' }}>{c.description}</p>}
            <FormBlock content={c} t={t} />
          </div>
        </section>
      );
    }

    default: {
      const headline = c.headline || c.title;
      const body = c.subheadline || c.description || c.text;
      if (!headline && !body) return null;
      return (
        <section style={style}>
          <div style={{ ...container, textAlign: 'center' }}>
            {headline && <h2 style={{ fontSize: 28, color: t.ink, margin: 0 }}>{headline}</h2>}
            {body && <p style={{ fontSize: 16, color: t.muted, marginTop: 12 }}>{body}</p>}
          </div>
        </section>
      );
    }
  }
}

export default function PublicBlockRenderer({
  page,
  loginUrl,
  loginLabel = 'Log in',
  signupUrl,
}: {
  page: PublicSitePage;
  loginUrl: string;
  loginLabel?: string;
  signupUrl?: string;
}) {
  const t = deriveTheme(page);
  const blocks = asArray(page.content_blocks)
    .filter((b) => b && b.isVisible !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  // Pixel-exact Triveny hero: when a designed page leads with a `triveny_hero`
  // block, render the canonical TrivenyHero standalone (full-bleed, its own
  // nav/footer) — identical to the built-in fallback. Editable copy (the three
  // pillar statements) rides in the block's content.statements.
  const lead = blocks[0];
  if (lead && (lead.blockType || lead.type) === 'triveny_hero') {
    const c = (lead.content || {}) as { statements?: Partial<Record<Pillar, string>> };
    return (
      <TrivenyHero
        loginUrl={loginUrl}
        loginLabel={loginLabel}
        signupUrl={signupUrl}
        statements={c.statements}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', fontFamily: t.font }}>
      <div style={{ position: 'fixed', top: 18, right: 22, zIndex: 50, display: 'flex', alignItems: 'center', gap: 10 }}>
        <a
          href={loginUrl}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: t.ink,
            textDecoration: 'none',
            background: 'rgba(255,255,255,.85)',
            backdropFilter: 'blur(8px)',
            padding: '8px 16px',
            borderRadius: 999,
            border: '1px solid rgba(20,23,28,.1)',
          }}
        >
          {loginLabel}
        </a>
        {signupUrl && (
          <a
            href={signupUrl}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              textDecoration: 'none',
              background: t.primary,
              padding: '8px 16px',
              borderRadius: 999,
            }}
          >
            Get started
          </a>
        )}
      </div>
      {blocks.map((b, i) => (
        <Block key={b.id || i} block={b} t={t} />
      ))}
    </div>
  );
}

// Reused by the Studio AI preview to render generated blocks visually
// (same renderer the public site uses, so the preview matches what ships).
// Renders a Web Presence form embedded on a page. Fields are snapshotted into
// the block content at pick-time (content.fields), so it renders identically in
// the editor and on the public site. Submission posts to the public form-submit
// endpoint, resolving the tenant from the current host.
function FormBlock({ content, t }: { content: AnyBlock; t: Theme }) {
  const fields: AnyBlock[] = asArray(content.fields);
  const [values, setValues] = useState<Record<string, any>>({});
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  if (!content.formId) {
    return <p style={{ fontSize: 14, color: t.muted }}>No form selected — pick one in the block settings.</p>;
  }
  if (!fields.length) {
    return <p style={{ fontSize: 14, color: t.muted }}>This form has no fields yet.</p>;
  }

  const set = (name: string, v: any) => setValues((p) => ({ ...p, [name]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending'); setMsg('');
    try {
      const base = config.weaveinApi?.baseUrl || '';
      const res = await fetch(`${base}/api/public/site/form/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: typeof window !== 'undefined' ? window.location.host : '',
          form_id: content.formId,
          data: values,
          source_url: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });
      const data = res.ok ? await res.json() : null;
      if (data?.ok) { setState('done'); setMsg(data.success_message || content.successMessage || 'Thanks — your submission was received.'); }
      else { setState('error'); setMsg('Sorry, that didn’t go through. Please try again.'); }
    } catch {
      setState('error'); setMsg('Sorry, that didn’t go through. Please try again.');
    }
  };

  if (state === 'done') {
    return <p style={{ fontSize: 16, color: t.ink, padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>{msg}</p>;
  }

  const inputCss: CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #d7dbe0', borderRadius: 8, fontSize: 15, marginTop: 4 };
  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      {fields.map((f, i) => {
        const name = f.name || f.id || `field_${i}`;
        const label = f.label || f.name || name;
        const opts = asArray(f.options).map((o: any) => (typeof o === 'string' ? { label: o, value: o } : o));
        return (
          <label key={name} style={{ fontSize: 13, color: t.ink, fontWeight: 600 }}>
            {label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}
            {f.type === 'textarea' ? (
              <textarea required={!!f.required} placeholder={f.placeholder} value={values[name] || ''} onChange={(e) => set(name, e.target.value)} rows={4} style={{ ...inputCss, resize: 'vertical' }} />
            ) : f.type === 'select' ? (
              <select required={!!f.required} value={values[name] || ''} onChange={(e) => set(name, e.target.value)} style={inputCss}>
                <option value="">Select…</option>
                {opts.map((o: any, j: number) => <option key={j} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === 'checkbox' ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontWeight: 400 }}>
                <input type="checkbox" checked={!!values[name]} onChange={(e) => set(name, e.target.checked)} /> {f.placeholder || 'Yes'}
              </span>
            ) : (
              <input type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'} required={!!f.required} placeholder={f.placeholder} value={values[name] || ''} onChange={(e) => set(name, e.target.value)} style={inputCss} />
            )}
          </label>
        );
      })}
      {state === 'error' && <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{msg}</p>}
      <button type="submit" disabled={state === 'sending'} style={{ padding: '11px 18px', borderRadius: 8, border: 'none', background: t.primary, color: '#fff', fontSize: 15, fontWeight: 600, cursor: state === 'sending' ? 'default' : 'pointer', justifySelf: 'start' }}>
        {state === 'sending' ? 'Sending…' : (content.submitLabel || 'Submit')}
      </button>
    </form>
  );
}

export { Block as PublicSiteBlock, DEFAULTS as PUBLIC_DEFAULT_THEME };
export type { Theme as PublicSiteTheme };
